/**
 * Iter-146 · Single source of truth for entity + industry types.
 *
 * The industry keys MUST match `backend/seed.py::INDUSTRY_EXCLUDES` (and
 * `INDUSTRY_FEATURES`) — they drive Chart-of-Accounts seeding, module
 * expansion, and the sidebar's industry-features gate. If you change a
 * key here, change it there too.
 *
 * Consumers: Signup (initial account creation), OrgSwitcher (Add Company
 * while logged in), Settings › Company (retroactive industry change).
 */

export const ENTITY_TYPES = [
  { v: "proprietorship",  l: "Proprietorship" },
  { v: "partnership",     l: "Partnership Firm" },
  { v: "llp",             l: "Limited Liability Partnership (LLP)" },
  { v: "private_limited", l: "Private Limited (Pvt Ltd)" },
  { v: "public_limited",  l: "Public Limited" },
  { v: "opc",             l: "One-Person Company (OPC)" },
];

// Canonical 6 industries — MUST match backend/seed.py INDUSTRY_EXCLUDES keys.
export const INDUSTRY_TYPES = [
  { v: "trading_inventory",    l: "Trading",                          sub: "Buy → sell goods · inventory ON" },
  { v: "trading_service",      l: "Trading + Service",                sub: "Goods & services · inventory ON" },
  { v: "service_no_inv",       l: "Service (no inv)",                 sub: "Consulting / SaaS · no stock" },
  { v: "service_inv",          l: "Service (with inv)",               sub: "Services + stocked items" },
  { v: "mfg_service_prod",     l: "Mfg + Service · Production",       sub: "RM→WIP→FG · full production module" },
  { v: "mfg_service_no_prod",  l: "Mfg + Service · No Production",    sub: "Stock manufacturing without shop-floor" },
];

// Feb-2026 · Founder-signed 5-option base business type (excluding add-ons).
// Add-ons (Distribution / Dealership) layer on top via separate checkboxes —
// see config/business_type_matrix.py on the backend.  The `industry` field
// remains the legacy Chart-of-Accounts seed driver; `business_type` drives
// the feature-applicability matrix (Sidebar hides, API gates).
export const BUSINESS_TYPES = [
  { v: "trading_with_inventory",         l: "Trading with Inventory",
    sub: "Buy → sell goods · full inventory + P2P + O2C", industry: "trading_inventory" },
  { v: "service_with_inventory",         l: "Service with Inventory",
    sub: "Services delivered · stocked spares / consumables", industry: "service_inv" },
  { v: "service_without_inventory",      l: "Service without Inventory",
    sub: "Consulting / SaaS · no stock · no inventory module", industry: "service_no_inv" },
  { v: "trading_service_both_inventory", l: "Trading + Service (both with Inventory)",
    sub: "Goods & services · inventory ON for both", industry: "trading_service" },
  { v: "mfg_service_trading",            l: "Manufacturing + Service + Trading",
    sub: "RM → WIP → FG · shop-floor · services · resale", industry: "mfg_service_prod" },
];

export const isManufacturingIndustry = (v) =>
  /^mfg_|manufactur/i.test(v || "");
